import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError } from 'rxjs';

import { CustomerAuthService, TokenStore } from '@pryspace/shared/api';

/**
 * Paths that must NEVER receive an Authorization header.
 *  - /v1/public/*                    — public identity surface
 *  - /v1/customer/auth/login/        — credentials in body
 *  - /v1/customer/auth/refresh/      — refresh token in body
 *  - /v1/shared/handoff/exchange/    — code-for-token exchange (no source token)
 */
const PUBLIC_PATTERNS: RegExp[] = [
  /\/v1\/public\//,
  /\/v1\/customer\/auth\/login\//,
  /\/v1\/customer\/auth\/refresh\//,
  /\/v1\/shared\/handoff\/exchange\//,
];

function pathOf(url: string): string {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url;
  }
}

function isPublic(url: string): boolean {
  const p = pathOf(url);
  return PUBLIC_PATTERNS.some((r) => r.test(p));
}

function attach(req: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
  if (!token) return req;
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

/**
 * Two-audience model: pryspace only ever holds a customer JWT. Tenant scoping
 * lives in the URL (`/v1/tenants/<slug>/...`) and is enforced server-side, so
 * we attach the same customer token to every authenticated request.
 */
export const httpInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const tokens = inject(TokenStore);
  const auth = inject(CustomerAuthService);

  if (isPublic(req.url)) {
    return next(req);
  }

  const initial = attach(req, tokens.getCustomerAccess());

  return next(initial).pipe(
    catchError((error) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }
      return from(auth.refresh()).pipe(
        switchMap((pair) => {
          if (!pair) {
            router.navigateByUrl('/login');
            return throwError(() => error);
          }
          return next(attach(req, tokens.getCustomerAccess()));
        }),
      );
    }),
  );
};
